import type { PropsWithChildren } from 'react'
import { NextStepReact, useNextStep } from 'nextstepjs'
import { useReactRouterAdapter } from 'nextstepjs/adapters/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/services/analytics'
import { PRODUCT_GALLERY_ONBOARDING_COOKIE, readProductGalleryOnboardingStatus, writeProductGalleryOnboardingStatus } from './product-gallery-onboarding.cookie'
import { buildProductGalleryOnboardingSteps, PRODUCT_GALLERY_ONBOARDING_TOUR } from './product-gallery-onboarding.steps'
import { ProductGalleryOnboardingTourCard } from './ProductGalleryOnboardingTourCard'
import { ProductGalleryOnboardingWelcomeModal } from './ProductGalleryOnboardingWelcomeModal'

const ONBOARDING_TOUR_DISPLAYED_KEY = 'offer_catalog.onboarding_tour_displayed'

export function ProductGalleryOnboardingController({ children }: Readonly<PropsWithChildren>) {
  const { t } = useTranslation()
  const { season } = useParams<{ season: string }>()
  const { startNextStep, currentTour } = useNextStep()
  const hasSeenOnboarding = readProductGalleryOnboardingStatus(PRODUCT_GALLERY_ONBOARDING_COOKIE)
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(!hasSeenOnboarding)

  useEffect(() => {
    if (isWelcomeOpen && !window.sessionStorage.getItem(ONBOARDING_TOUR_DISPLAYED_KEY)) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.ONBOARDING_TOUR_DISPLAYED, { season })
      window.sessionStorage.setItem(ONBOARDING_TOUR_DISPLAYED_KEY, 'true')
    }
  }, [isWelcomeOpen, season])

  const tourSteps = useMemo(
    () => buildProductGalleryOnboardingSteps(t),
    [t],
  )

  const dismissTour = () => {
    writeProductGalleryOnboardingStatus('dismissed', PRODUCT_GALLERY_ONBOARDING_COOKIE)
    setIsWelcomeOpen(false)
  }

  const completeTour = () => {
    writeProductGalleryOnboardingStatus('completed', PRODUCT_GALLERY_ONBOARDING_COOKIE)
    trackAnalyticsEvent(ANALYTICS_EVENTS.ONBOARDING_TOUR_COMPLETED, { season })
  }

  const startTour = () => {
    setIsWelcomeOpen(false)
    startNextStep(PRODUCT_GALLERY_ONBOARDING_TOUR)
  }

  return (
    <NextStepReact
      steps={tourSteps}
      cardComponent={ProductGalleryOnboardingTourCard}
      onComplete={completeTour}
      onSkip={dismissTour}
      navigationAdapter={useReactRouterAdapter}
      shadowOpacity="0.55"
      disableConsoleLogs
      showNextStep
    >
      <ProductGalleryOnboardingWelcomeModal
        isOpen={isWelcomeOpen}
        onDismiss={dismissTour}
        onStart={startTour}
      />
      <div
        id="product-gallery-onboarding-viewport"
        {...currentTour && { style: { overflow: 'auto' } }}
      >
        {children}
      </div>
    </NextStepReact>
  )
}
